import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"
import { Loader2 } from "lucide-react"

import { cn } from "../../lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded text-sm font-normal transition-colors focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50 [&_svg]:h-3.5 [&_svg]:w-3.5",
  {
    variants: {
      variant: {
        default:
          "border border bg-background text-foreground shadow-sm hover:bg-accent hover:border-primary hover:text-primary",
        primary:
          "bg-primary text-white shadow-sm hover:bg-primary/90",
        destructive:
          "bg-red-500 text-white shadow-sm hover:bg-red-600",
        outline:
          "border border bg-transparent hover:bg-accent",
        ghost:
          "hover:bg-muted",
        link:
          "text-primary underline-offset-4 hover:underline",
        segment:
          "bg-white text-foreground shadow-sm border border-border/50 rounded-md hover:bg-accent dark:bg-muted dark:border-transparent dark:hover:bg-zinc-800",
      },
      size: {
        default: "h-8 px-4 py-1",
        sm: "h-7 px-3 text-xs",
        lg: "h-10 px-6",
        icon: "h-8 w-8 p-0",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  loading?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, loading, disabled, children, ...props }, ref) => {
    return (
      <button
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        disabled={disabled || loading}
        {...props}
      >
        {loading && <Loader2 className="animate-spin" />}
        {children}
      </button>
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }
